import { useEffect, useRef, useState } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { mergeEvents } from '@/app/lib/quant/liveEvents'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isLiveGone, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import type { LiveEvent } from '@/app/lib/quant/types'

export const EVENTS_PAGE_LIMIT = 1000
export const EVENTS_POLL_MS = 4000

export interface QuantLiveEventsState {
  /** Ascending by `seq`; at most the newest 5,000. */
  events: LiveEvent[]
  /** Lines the server could not read, summed over every page. */
  skipped: number
  /** The latest answer's `problem`, or null. */
  problem: string | null
  /** True once older events were dropped to stay within the memory limit. */
  droppedOlder: boolean
  /** Set while the latest read failed; the events stay. */
  error?: string
  notFound: boolean
  loaded: boolean
}

const INITIAL: QuantLiveEventsState = {
  events: [],
  skipped: 0,
  problem: null,
  droppedOlder: false,
  notFound: false,
  loaded: false,
}

/**
 * Spec 041: the event log of one session. The first read has no `after`; every next read
 * passes the previous answer's `next` unchanged. While `more` is true the next read follows
 * at once (one request at a time); while `live` is true and the session is `active` a read
 * comes every 4 s. When `active` turns false there is one final read, then it stops. Events
 * merge by `seq` (a repeated page adds nothing) and only the newest 5,000 are kept. A failed
 * read keeps the events and backs off 2, 4, 8, 15 s; nothing is requested while the tab is
 * hidden and a read happens at once when it becomes visible.
 */
export function useQuantLiveEvents(id: string, active: boolean): QuantLiveEventsState {
  const [state, setState] = useState<QuantLiveEventsState>(INITIAL)
  const activeRef = useRef(active)
  const prevActive = useRef(active)
  const finalRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    activeRef.current = active
    if (prevActive.current && !active) finalRef.current?.()
    prevActive.current = active
  }, [active])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null
    let inFlight = false
    let failures = 0
    let cursor: number | null = null
    let held: LiveEvent[] = []
    let skipped = 0
    let dropped = false
    let done = false
    let complete = false
    let finalPending = false

    const isHidden = () => document.visibilityState === 'hidden'
    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const schedule = (ms: number) => {
      clearTimer()
      if (cancelled || done || isHidden()) return
      timer = setTimeout(read, ms)
    }

    function read() {
      timer = null
      if (cancelled || inFlight || isHidden()) return
      finalPending = false
      inFlight = true
      const own = new AbortController()
      controller = own
      quantApi.getLiveEvents(id, cursor, EVENTS_PAGE_LIMIT, own.signal).then(
        (page) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures = 0
          const merged = mergeEvents(held, Array.isArray(page.events) ? page.events : [])
          held = merged.events
          dropped = dropped || merged.droppedOlder
          skipped += typeof page.skipped === 'number' ? page.skipped : 0
          cursor = page.next
          complete = !page.live && !page.more
          setState({
            events: held,
            skipped,
            problem: page.problem ?? null,
            droppedOlder: dropped,
            notFound: false,
            loaded: true,
          })
          if (page.more) read()
          else if (finalPending) read()
          else if (page.live && activeRef.current) schedule(EVENTS_POLL_MS)
          else done = true
        },
        (error: unknown) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          if (isLiveGone(error)) {
            done = true
            setState((prev) => ({ ...prev, notFound: true, loaded: true }))
            return
          }
          failures += 1
          const message = quantErrorMessage(error)
          setState((prev) => ({ ...prev, error: message }))
          schedule(nextDelay(failures))
        },
      )
    }

    // The session ended: one last read, unless the server already said the log is complete.
    finalRef.current = () => {
      if (cancelled || complete) return
      finalPending = true
      done = false
      clearTimer()
      if (!inFlight) read()
    }

    function onVisibility() {
      if (cancelled) return
      if (isHidden()) {
        clearTimer()
        return
      }
      if ((!done || finalPending) && !inFlight) {
        clearTimer()
        read()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    read()
    return () => {
      cancelled = true
      clearTimer()
      controller?.abort()
      finalRef.current = null
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [id])

  return state
}
